import styled from '@emotion/styled';
import React, { ChangeEvent } from 'react';
import {
	ITheme,
	Markdown,
	PaperSelector,
	TextButton,
} from '../../../../../../ui';
import { getFormattedPrice } from '../../../../../shared/i18n/price/utils/getFormattedPrice';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { IParkingProductUpgrade } from '../../../../domain/parking-product/models/IParkingProductUpgrade';
import Responsive from '../../../../../../ui/utils/Responsive';
import themed from '../../../../../../ui/themer/themed';

interface IUpgradeMainProps {
	isSelected: boolean;
	handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
	upgrade: IParkingProductUpgrade;
}

export const UpgradeMain = ({
	isSelected,
	handleChange,
	upgrade,
}: IUpgradeMainProps) => {
	const { t } = useTranslation();

	const buttonText = isSelected
		? t('parking_booking_upgrades_remove_upgrade')
		: t('parking_booking_upgrades_add_upgrade');

	return (
		<StyledUpgrade
			type="checkbox"
			selected={isSelected}
			onChange={handleChange}
		>
			<Header>
				<h3>{upgrade.name}</h3>
				<Price>{getFormattedPrice(upgrade.price.upgradeCost)}</Price>
			</Header>

			<Body>
				<Description>
					{upgrade.description && <Markdown source={upgrade.description} />}
				</Description>
				<CustomTextButton variant="highlight">{buttonText}</CustomTextButton>
			</Body>
		</StyledUpgrade>
	);
};

const CustomTextButton = TextButton.withComponent('div');

const StyledUpgrade = styled(PaperSelector)`
	margin-bottom: 32px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: 24px;
	}
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;

	h3 {
		flex-grow: 1;
	}
`;

const Body = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-end;

	${CustomTextButton} {
		white-space: nowrap;
		min-width: 160px;
		text-align: right;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;

		${TextButton} {
			margin-left: auto;
		}
	}
`;

const Description = styled.div`
	flex-grow: 1;
`;

const Price = styled.div<{}, ITheme>`
	font-size: ${themed(({ typography }) => typography.header.h3.size)};
	font-weight: ${themed(({ typography }) => typography.header.h3.fontWeight)};
`;
